/** WebGL2 shader runtime for adjustment / filter passes.
 *
 *  ── Why a custom runtime ────────────────────────────────────────
 *  The CPU adjustment dispatch (`lib/adjustments/dispatch.ts`) does
 *  a per-pixel JS loop for each kind. On a 4K canvas that's ~16M
 *  pixel ops per pass — at JS speeds (~10-50M ops/sec depending on
 *  the kind), a single Color-Balance pass is 300-1500ms. Stack 3
 *  filters and the user sees 1-5 second freezes per slider tick.
 *
 *  Moving the math to a WebGL2 fragment shader brings the per-pass
 *  cost into the single-digit-ms range on a 4K canvas (GPU is
 *  embarrassingly parallel for per-pixel ops). That's the only way
 *  to get pro-grade live-preview performance with non-destructive
 *  adjustment layers — every editor that nails this (Photopea,
 *  Photoshop Web, Affinity Photo) uses the same approach.
 *
 *  ── Architecture ────────────────────────────────────────────────
 *  One shared WebGL2 context attached to an offscreen canvas, lazily
 *  initialized on first use. Each single-pass adjustment:
 *    1. Resize the GL canvas to match the source dims.
 *    2. Upload source pixels as a texture.
 *    3. Run the kind-specific fragment shader against a fullscreen
 *       quad (single vertex buffer, reused across passes).
 *    4. `drawImage` the GL canvas onto the target canvas — same-size
 *       blit, GPU path, no CPU readback.
 *
 *  Programs are cached by fragment-shader source string so the
 *  per-kind compile cost is paid once per session.
 *
 *  Two public entry points:
 *    - `applyShader` — single-pass shader. Used by LUT-based,
 *      HSL-space, direct-RGB, single-pass spatial, UV-remap, and
 *      single-pass convolution (Motion Blur) kinds.
 *    - `applyShaderPasses` — multi-pass chain. Pass 0 reads the
 *      input source; non-final passes render to framebuffer-backed
 *      "ping-pong" textures held in the runtime state; the final
 *      pass writes to the GL canvas, which is blitted to target.
 *      Used by separable convolution kinds (Gaussian, Box) and
 *      future ≥ 2-pass shaders (Unsharp Mask, Smart Sharpen).
 *      Single-pass requests forward to `applyShader` to skip the
 *      FBO setup cost.
 *
 *  ── Failure modes ───────────────────────────────────────────────
 *  Every public entry point can return `false` / `null` to mean
 *  "couldn't run on GPU; fall back to JS." Caller MUST honor this:
 *    - getContext('webgl2') fails (very old hardware, headless tests,
 *      software-rendered fallback disabled).
 *    - Shader compile fails (driver bugs).
 *    - Texture allocation fails (VRAM exhausted on huge images).
 *  None of these are fatal to the editor — JS path stays correct.
 *
 *  ── Memory ──────────────────────────────────────────────────────
 *  Textures are GC'd by `gl.deleteTexture` at the end of each pass.
 *  We don't pool them (yet) because the size varies per call and
 *  pooling cross-size adds complexity. The GL canvas itself is
 *  reused. The compile cache holds program objects forever — they
 *  are cheap (no texture memory). */

const VERTEX_SHADER_SRC = `#version 300 es
in vec2 a_position;
out vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

interface CompiledProgram {
  program: WebGLProgram;
  attribPosition: number;
}

/** A scalar/vector value to bind to a fragment-shader uniform. Each
 *  variant maps to the matching `uniformN[fi]` / `uniform[N]fv` call.
 *  Sampler bindings are handled internally by the runtime — callers
 *  declaring `sampler2D u_source` get the source texture wired
 *  automatically. */
export type ShaderUniform =
  | { type: 'float'; value: number }
  | { type: 'int'; value: number }
  | { type: 'vec2'; value: readonly [number, number] }
  | { type: 'vec3'; value: readonly [number, number, number] }
  | { type: 'vec4'; value: readonly [number, number, number, number] }
  | { type: 'float[]'; value: readonly number[] };

export interface ApplyShaderRequest {
  /** The pixel source. The runtime uploads it as `u_source`. Texture
   *  dimensions follow the source's pixel dims. */
  source: HTMLCanvasElement | ImageData;
  /** Where the GPU output gets blitted via drawImage. Resized to
   *  match the runtime's output dims if needed. */
  target: HTMLCanvasElement;
  /** Fragment-shader source. Must declare `precision highp float;`,
   *  `in vec2 v_uv;`, `uniform sampler2D u_source;`, and `out vec4
   *  outColor;`. The runtime appends nothing. */
  fragShaderSrc: string;
  /** Uniform values keyed by uniform name (without the `u_` prefix
   *  convention — keys here are exact uniform names as declared in
   *  the shader, minus `u_source` which the runtime binds itself). */
  uniforms: Record<string, ShaderUniform>;
  /** Override the output framebuffer dims. Default: matches source.
   *  Set this when the shader's output is intentionally a different
   *  size from the input — e.g. Pixelate's downsample pass writes a
   *  (W/N, H/N) image from a full-size source. The GL canvas + target
   *  resize to `targetDims`; the texture upload still uses source
   *  dims so the shader can sample at sub-texel positions
   *  corresponding to source pixels. */
  targetDims?: { width: number; height: number };
  /** Texture filtering mode for `u_source`. Default `nearest` for
   *  byte-exact sampling — what every adjustment shader so far
   *  needs (the LUT-based kinds, the HSL-space kinds, the
   *  per-pixel direct-RGB filters). UV-remap kinds (Twirl, Pinch,
   *  Lens Correction, future warp filters) want `linear` so that
   *  hardware bilinear sampling matches the JS path's
   *  `bilinearSample` helper for sub-texel sample positions. */
  filter?: 'nearest' | 'linear';
}

/** Framebuffer-backed texture used by `applyShaderPasses` to chain
 *  multi-pass shaders. Created lazily on first use, re-allocated
 *  when source dimensions change between calls. */
interface FboTexture {
  texture: WebGLTexture;
  framebuffer: WebGLFramebuffer;
  width: number;
  height: number;
}

interface RuntimeState {
  gl: WebGL2RenderingContext;
  canvas: HTMLCanvasElement;
  quadBuffer: WebGLBuffer;
  programCache: Map<string, CompiledProgram>;
  /** Ping-pong textures for multi-pass shaders. `pingPongA` is
   *  allocated on the first multi-pass call; `pingPongB` is
   *  allocated lazily only when an N-pass shader with N >= 3 runs
   *  (2-pass shaders never alternate onto the B slot — they write
   *  pass 0's output to A and pass 1 reads from A while writing to
   *  the canvas). Both are re-allocated when source dims change.
   *  Two slots are enough for any N via parity alternation. */
  pingPongA: FboTexture | null;
  pingPongB: FboTexture | null;
}

let runtime: RuntimeState | null = null;
let runtimeInitFailed = false;

function initRuntime(): RuntimeState | null {
  if (runtime) return runtime;
  if (runtimeInitFailed) return null;
  // Defensive: SSR / non-browser environments.
  if (typeof document === 'undefined') {
    runtimeInitFailed = true;
    return null;
  }
  // The whole init is wrapped in try/catch because happy-dom (and
  // some headless test environments) returns a *partial* WebGL2
  // mock: getContext('webgl2') succeeds, but downstream methods
  // like `createBuffer` are missing. We don't want to crash the
  // CPU dispatch when that happens — failing the init here lets
  // `isGpuAvailable()` report false cleanly and every caller falls
  // back to JS.
  try {
    const canvas = document.createElement('canvas');
    // `premultipliedAlpha: false` matches what the canvas2d pipeline
    // does — RGBA8 with straight alpha. `preserveDrawingBuffer: false`
    // lets the driver swap aggressively (we drawImage out immediately
    // after each pass).
    const gl = canvas.getContext('webgl2', {
      alpha: true,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
      antialias: false,
      depth: false,
      stencil: false,
    }) as WebGL2RenderingContext | null;
    if (!gl) {
      runtimeInitFailed = true;
      return null;
    }
    // Probe for the entry points we depend on. Partial mocks /
    // limited drivers can surface here without ever throwing.
    if (
      typeof gl.createBuffer !== 'function' ||
      typeof gl.bindBuffer !== 'function' ||
      typeof gl.bufferData !== 'function' ||
      typeof gl.createTexture !== 'function' ||
      typeof gl.texImage2D !== 'function' ||
      typeof gl.createProgram !== 'function' ||
      typeof gl.createShader !== 'function' ||
      typeof gl.drawArrays !== 'function' ||
      // Multi-pass entry points — only used by applyShaderPasses but
      // required for any 2+ pass shader to work. Probed at init so a
      // single capability check fails the whole runtime cleanly.
      typeof gl.createFramebuffer !== 'function' ||
      typeof gl.bindFramebuffer !== 'function' ||
      typeof gl.framebufferTexture2D !== 'function' ||
      typeof gl.checkFramebufferStatus !== 'function'
    ) {
      runtimeInitFailed = true;
      return null;
    }
    const quadBuffer = gl.createBuffer();
    if (!quadBuffer) {
      runtimeInitFailed = true;
      return null;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    // Fullscreen quad: two triangles in clip space. The vertex shader
    // remaps to UV [0, 1] for texture sampling.
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );
    runtime = {
      gl,
      canvas,
      quadBuffer,
      programCache: new Map(),
      pingPongA: null,
      pingPongB: null,
    };
    return runtime;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[image-editor/gpu] runtime init failed:', err);
    }
    runtimeInitFailed = true;
    return null;
  }
}

function compileShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    if (import.meta.env.DEV) {
      console.warn(
        '[image-editor/gpu] shader compile failed:',
        gl.getShaderInfoLog(shader),
        '\nsource:\n',
        src,
      );
    }
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function getOrCompileProgram(state: RuntimeState, fragShaderSrc: string): CompiledProgram | null {
  const cached = state.programCache.get(fragShaderSrc);
  if (cached) return cached;
  const { gl } = state;
  const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER_SRC);
  if (!vs) return null;
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragShaderSrc);
  if (!fs) {
    gl.deleteShader(vs);
    return null;
  }
  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    return null;
  }
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  // Shaders are no longer needed once linked.
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    if (import.meta.env.DEV) {
      console.warn('[image-editor/gpu] program link failed:', gl.getProgramInfoLog(program));
    }
    gl.deleteProgram(program);
    return null;
  }
  const attribPosition = gl.getAttribLocation(program, 'a_position');
  const compiled: CompiledProgram = { program, attribPosition };
  state.programCache.set(fragShaderSrc, compiled);
  return compiled;
}

function bindUniforms(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  uniforms: Record<string, ShaderUniform>,
): void {
  for (const name in uniforms) {
    const loc = gl.getUniformLocation(program, name);
    if (loc === null) continue; // Silently skip unused uniforms.
    const u = uniforms[name];
    switch (u.type) {
      case 'float':
        gl.uniform1f(loc, u.value);
        break;
      case 'int':
        gl.uniform1i(loc, u.value);
        break;
      case 'vec2':
        gl.uniform2f(loc, u.value[0], u.value[1]);
        break;
      case 'vec3':
        gl.uniform3f(loc, u.value[0], u.value[1], u.value[2]);
        break;
      case 'vec4':
        gl.uniform4f(loc, u.value[0], u.value[1], u.value[2], u.value[3]);
        break;
      case 'float[]':
        gl.uniform1fv(loc, u.value);
        break;
    }
  }
}

/** True when a WebGL2 context can be created in this environment.
 *  Memoizes the lazy init result so callers can guard cheaply per
 *  call. Returns false in headless / SSR / very old browsers; the
 *  CPU dispatch handles those paths. */
export function isGpuAvailable(): boolean {
  return initRuntime() !== null;
}

/** Run a single-pass fragment shader, blit the result into `target`.
 *  Returns `true` on success, `false` to mean "caller should fall
 *  back to JS dispatch." False is non-fatal — the target canvas is
 *  unchanged on failure.
 *
 *  Width / height come from the source. The target is resized to
 *  match if needed; same contract as the JS dispatch's
 *  `applyAdjustmentToCanvas` helpers. */
export function applyShader(req: ApplyShaderRequest): boolean {
  const state = initRuntime();
  if (!state) return false;
  const { gl, canvas, quadBuffer } = state;
  const srcW = req.source instanceof ImageData ? req.source.width : req.source.width;
  const srcH = req.source instanceof ImageData ? req.source.height : req.source.height;
  if (srcW <= 0 || srcH <= 0) return false;
  // Output dims: default to source dims; callers can override via
  // `targetDims` when the shader's output is a different size than
  // the input (e.g. Pixelate's downsample pass).
  const outW = req.targetDims ? req.targetDims.width : srcW;
  const outH = req.targetDims ? req.targetDims.height : srcH;
  if (outW <= 0 || outH <= 0) return false;
  // Resize the GL backing canvas to match the OUTPUT dims (where the
  // shader writes). WebGL caches viewport/scissor state, so we set
  // viewport explicitly.
  if (canvas.width !== outW) canvas.width = outW;
  if (canvas.height !== outH) canvas.height = outH;
  gl.viewport(0, 0, outW, outH);

  const program = getOrCompileProgram(state, req.fragShaderSrc);
  if (!program) return false;

  // Upload source as the sampler2D `u_source`. Texture is one-shot
  // for this pass — created, bound, used, deleted.
  const tex = gl.createTexture();
  if (!tex) return false;
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  // Canvas2D pixels are straight (non-premultiplied) RGBA8. Flip Y
  // because WebGL's default texture origin is bottom-left while
  // canvas2d / ImageData is top-left — without this, the output
  // would render upside-down.
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  try {
    if (req.source instanceof ImageData) {
      // texImage2D(target, level, internalFormat, width, height, border, format, type, ImageData)
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        srcW,
        srcH,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        req.source.data,
      );
    } else {
      // texImage2D(target, level, internalFormat, format, type, HTMLCanvasElement)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, req.source);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[image-editor/gpu] texImage2D failed:', err);
    }
    gl.deleteTexture(tex);
    return false;
  }
  // CLAMP_TO_EDGE so sampling at the border doesn't wrap; LINEAR for
  // intra-pixel smoothness (rarely matters for byte-precision LUT
  // ops, but cheap and safer for future filters that fractional-
  // sample like blur kernels).
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  // Default to NEAREST so existing shaders keep their byte-exact
  // sampling. UV-remap kinds opt into LINEAR so the GPU's hardware
  // bilinear interpolator does the same 2×2 weighted average the
  // CPU `bilinearSample` helper computes.
  const filterMode = req.filter === 'linear' ? gl.LINEAR : gl.NEAREST;
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filterMode);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filterMode);

  gl.useProgram(program.program);
  // Bind the fullscreen quad.
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.enableVertexAttribArray(program.attribPosition);
  gl.vertexAttribPointer(program.attribPosition, 2, gl.FLOAT, false, 0, 0);

  // Wire u_source → texture unit 0.
  const sourceLoc = gl.getUniformLocation(program.program, 'u_source');
  if (sourceLoc !== null) gl.uniform1i(sourceLoc, 0);
  bindUniforms(gl, program.program, req.uniforms);

  // Single full-screen draw call.
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  // Blit GL canvas → target canvas. Resize target if needed so the
  // contract matches the JS path. drawImage is GPU-accelerated for
  // canvas-to-canvas same-size copies.
  if (req.target.width !== outW) req.target.width = outW;
  if (req.target.height !== outH) req.target.height = outH;
  const tCtx = req.target.getContext('2d');
  if (!tCtx) {
    gl.deleteTexture(tex);
    return false;
  }
  // The WebGL canvas may have stale pixels in border areas if it was
  // larger before; clear the target to transparent before copying so
  // any residual content doesn't leak through.
  tCtx.clearRect(0, 0, outW, outH);
  tCtx.drawImage(canvas, 0, 0);

  gl.deleteTexture(tex);
  return true;
}

/** One step of a multi-pass shader chain. Same shape as
 *  `applyShader`'s top-level config, minus the source/target/filter
 *  bits which are managed by the multi-pass runtime. */
export interface ShaderPass {
  /** Fragment-shader source for this pass. Must declare the same
   *  prelude as single-pass shaders (precision, in vec2 v_uv,
   *  uniform sampler2D u_source, out vec4 outColor). May also
   *  declare `uniform sampler2D u_original` to access the original
   *  input source — see the binding contract on
   *  `applyShaderPasses`. */
  fragShaderSrc: string;
  /** Uniforms for this pass. `u_source` is wired by the runtime —
   *  for pass 0 it's the input source; for subsequent passes it's
   *  the previous pass's output texture. `u_original` (when
   *  declared) is always the original input source. */
  uniforms: Record<string, ShaderUniform>;
}

/** Configuration for a multi-pass shader chain. */
export interface ApplyShaderPassesRequest {
  source: HTMLCanvasElement | ImageData;
  target: HTMLCanvasElement;
  /** Sequence of passes. Pass 0 reads `source`. Pass N reads pass
   *  (N-1)'s output. The final pass writes to the GL canvas, which
   *  is then blitted to `target` via drawImage. */
  passes: ShaderPass[];
  /** Texture-filter mode for input samples in EVERY pass. Convolution
   *  kinds typically want `nearest`; UV-remap kinds want `linear`.
   *  Per-pass override is intentionally not supported here — a
   *  multi-pass chain that needs mixed filtering is a smell. */
  filter?: 'nearest' | 'linear';
}

/** Allocate (or re-allocate) a framebuffer-backed RGBA8 texture at
 *  the given dims. Reuses existing storage when dims match. Returns
 *  null on any allocation / framebuffer-completeness failure so the
 *  caller can fall through to CPU. */
function ensureFboTexture(
  gl: WebGL2RenderingContext,
  existing: FboTexture | null,
  width: number,
  height: number,
  filterMode: number,
): FboTexture | null {
  if (existing && existing.width === width && existing.height === height) {
    // Same size — keep the storage, just update filter mode.
    gl.bindTexture(gl.TEXTURE_2D, existing.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filterMode);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filterMode);
    return existing;
  }
  if (existing) {
    gl.deleteTexture(existing.texture);
    gl.deleteFramebuffer(existing.framebuffer);
  }
  const texture = gl.createTexture();
  const framebuffer = gl.createFramebuffer();
  if (!texture || !framebuffer) {
    if (texture) gl.deleteTexture(texture);
    if (framebuffer) gl.deleteFramebuffer(framebuffer);
    return null;
  }
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filterMode);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filterMode);
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    if (import.meta.env.DEV) {
      console.warn('[image-editor/gpu] FBO incomplete, status:', status);
    }
    gl.deleteTexture(texture);
    gl.deleteFramebuffer(framebuffer);
    return null;
  }
  return { texture, framebuffer, width, height };
}

/** Bind a program's vertex attribute + uniforms in one place. Used
 *  by both `applyShader` (single-pass) and `applyShaderPasses`
 *  (multi-pass) — pulled out so the two paths' binding sequences
 *  stay in lockstep. */
function bindProgramAndQuad(
  state: RuntimeState,
  program: CompiledProgram,
  uniforms: Record<string, ShaderUniform>,
): void {
  const { gl, quadBuffer } = state;
  gl.useProgram(program.program);
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.enableVertexAttribArray(program.attribPosition);
  gl.vertexAttribPointer(program.attribPosition, 2, gl.FLOAT, false, 0, 0);
  const sourceLoc = gl.getUniformLocation(program.program, 'u_source');
  if (sourceLoc !== null) gl.uniform1i(sourceLoc, 0);
  bindUniforms(gl, program.program, uniforms);
}

/** Multi-pass shader chain with ping-pong textures.
 *
 *  Pass 0 reads `source` (uploaded as a one-shot texture with
 *  UNPACK_FLIP_Y_WEBGL matching `applyShader`). Each subsequent
 *  pass reads the previous pass's output texture via `u_source`;
 *  non-final passes render to alternating ping-pong FBO-backed
 *  textures; the final pass renders to the GL canvas, which is
 *  blitted to `target`.
 *
 *  ── Texture units ───────────────────────────────────────────────
 *  Two samplers are exposed to every pass:
 *    - `u_source` (unit 0) — pass 0 sees the original input; later
 *      passes see the previous pass's output.
 *    - `u_original` (unit 1, optional) — the original input source,
 *      bound for the lifetime of the chain. Sharpen-style kinds
 *      (Unsharp Mask, Smart Sharpen) use this in their final pass
 *      to subtract a blurred copy from the unmodified source.
 *      Shaders that don't declare `u_original` pay nothing — the
 *      binding is conditional on the location being non-null.
 *
 *  Single-pass requests forward to `applyShader` to avoid the
 *  ping-pong setup cost for one-shot work.
 *
 *  Returns true on success. Returns false on any failure (no
 *  WebGL2, FBO incomplete, shader compile error, texture upload
 *  failure) — callers fall back to the CPU dispatch. Never
 *  throws. */
export function applyShaderPasses(req: ApplyShaderPassesRequest): boolean {
  if (req.passes.length === 0) return false;
  if (req.passes.length === 1) {
    // Single pass — defer to the simpler path (no FBO allocation).
    return applyShader({
      source: req.source,
      target: req.target,
      fragShaderSrc: req.passes[0].fragShaderSrc,
      uniforms: req.passes[0].uniforms,
      filter: req.filter,
    });
  }

  const state = initRuntime();
  if (!state) return false;
  const { gl, canvas, quadBuffer: _quadBuffer } = state;
  const srcW = req.source.width;
  const srcH = req.source.height;
  if (srcW <= 0 || srcH <= 0) return false;

  // Compile every pass's program upfront so we don't waste work
  // (texture upload, FBO writes) when a later pass fails to compile.
  const programs: CompiledProgram[] = [];
  for (const pass of req.passes) {
    const program = getOrCompileProgram(state, pass.fragShaderSrc);
    if (!program) return false;
    programs.push(program);
  }

  const filterMode = req.filter === 'linear' ? gl.LINEAR : gl.NEAREST;

  // Resize GL canvas to match source. Same convention as single-pass.
  if (canvas.width !== srcW) canvas.width = srcW;
  if (canvas.height !== srcH) canvas.height = srcH;
  gl.viewport(0, 0, srcW, srcH);

  // Allocate / reuse ping-pong textures sized to source dims. For
  // N passes we need (N - 1) intermediate textures, but two
  // persistent slots cover any N via parity alternation. 2-pass
  // shaders only ever write to A (pass 0's output) and read from
  // A (pass 1's input) — B never participates, so we skip its
  // allocation to avoid a wasted source-dim RGBA8 (~64 MB on a 4K
  // source). N-pass shaders with N >= 3 use both slots.
  state.pingPongA = ensureFboTexture(gl, state.pingPongA, srcW, srcH, filterMode);
  if (!state.pingPongA) return false;
  const intermediates: FboTexture[] = [state.pingPongA];
  if (req.passes.length >= 3) {
    state.pingPongB = ensureFboTexture(gl, state.pingPongB, srcW, srcH, filterMode);
    if (!state.pingPongB) return false;
    intermediates.push(state.pingPongB);
  }

  // Upload the input source as a one-shot texture (same shape as
  // applyShader). Released at the end.
  const sourceTex = gl.createTexture();
  if (!sourceTex) return false;
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, sourceTex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  try {
    if (req.source instanceof ImageData) {
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        srcW,
        srcH,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        req.source.data,
      );
    } else {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, req.source);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[image-editor/gpu] texImage2D failed (multi-pass source):', err);
    }
    gl.deleteTexture(sourceTex);
    return false;
  }
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filterMode);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filterMode);

  // Also bind the source on texture unit 1 for the lifetime of the
  // pass chain. Shaders that declare `uniform sampler2D u_original`
  // read it from here; shaders that don't declare it pay nothing
  // (the per-pass uniform1i bind is skipped when the location is
  // null). Restore active unit to 0 so the per-pass inputTex bind
  // below targets the right slot.
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, sourceTex);
  gl.activeTexture(gl.TEXTURE0);

  // Pass i reads inputTex; non-last passes write to an alternating
  // intermediate slot (A or B); the last pass writes to the default
  // framebuffer (the GL canvas, blitted to target below). Modulo on
  // `intermediates.length` keeps 2-pass shaders pinned to A and
  // alternates A/B for N >= 3.
  let inputTex: WebGLTexture = sourceTex;
  for (let i = 0; i < req.passes.length; i++) {
    const isLast = i === req.passes.length - 1;
    if (isLast) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    } else {
      const fbo = intermediates[i % intermediates.length];
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.framebuffer);
    }
    gl.viewport(0, 0, srcW, srcH);

    bindProgramAndQuad(state, programs[i], req.passes[i].uniforms);

    // Wire u_original → unit 1 if this pass's shader declares it.
    // Cheap probe; no-op when the uniform isn't present in the shader.
    const originalLoc = gl.getUniformLocation(programs[i].program, 'u_original');
    if (originalLoc !== null) gl.uniform1i(originalLoc, 1);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, inputTex);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    if (!isLast) {
      inputTex = intermediates[i % intermediates.length].texture;
    }
  }

  // Blit GL canvas → target.
  if (req.target.width !== srcW) req.target.width = srcW;
  if (req.target.height !== srcH) req.target.height = srcH;
  const tCtx = req.target.getContext('2d');
  if (!tCtx) {
    gl.deleteTexture(sourceTex);
    return false;
  }
  tCtx.clearRect(0, 0, srcW, srcH);
  tCtx.drawImage(canvas, 0, 0);

  gl.deleteTexture(sourceTex);
  return true;
}

/** Free the GL context and reset the singleton. Used by tests; not
 *  expected to be called in production. */
export function disposeGpuRuntime(): void {
  if (!runtime) return;
  const { gl, programCache, quadBuffer, pingPongA, pingPongB } = runtime;
  for (const { program } of programCache.values()) gl.deleteProgram(program);
  gl.deleteBuffer(quadBuffer);
  if (pingPongA) {
    gl.deleteTexture(pingPongA.texture);
    gl.deleteFramebuffer(pingPongA.framebuffer);
  }
  if (pingPongB) {
    gl.deleteTexture(pingPongB.texture);
    gl.deleteFramebuffer(pingPongB.framebuffer);
  }
  runtime = null;
  runtimeInitFailed = false;
}
